/**
 * RevenueDashboardPage.tsx — Revenue overview dashboard
 *
 * Purpose:
 *   Displays MRR/ARR/churn StatCards, a 12-month MRR trend LineChart,
 *   a stacked BarChart of MRR components, and a cohort retention heatmap.
 *
 * Data sources (AT-30.01):
 *   - GET /admin/reports/mrr  → useMrrReport()
 *   - GET /admin/reports/arr  → useArrReport()
 *   - GET /admin/reports/cohorts → useCohortRetention()
 *
 * @module packages/billing-admin/src/pages/RevenueDashboardPage
 * @since 2026-06-03 (AT-50.03 — Revenue Dashboard & Enterprise Screens)
 */
import React from 'react';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import StatCard from '../components/StatCard';
import CohortRetentionHeatmap from '../components/revenue/CohortRetentionHeatmap';
import {
  useMrrReport,
  useArrReport,
  useCohortRetention,
} from '../hooks/useAdminApi50';
import AdminLayout from '../components/AdminLayout';

/** Format a number as a compact dollar amount (e.g. $12.3K, $1.2M). */
function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(1)}K`;
  return `$${value.toFixed(0)}`;
}

/**
 * RevenueDashboardPage — MRR/ARR stats, trend chart, breakdown chart, and cohort heatmap.
 *
 * @since 2026-06-03 (AT-50.03)
 */
export default function RevenueDashboardPage() {
  const mrr = useMrrReport();
  const arr = useArrReport();
  const cohort = useCohortRetention();

  const latestMrr = mrr.data?.[mrr.data.length - 1];

  return (
    <AdminLayout title="Revenue">
      <div className="space-y-6">
        <section className="rounded-[28px] border border-white/70 bg-white/85 p-6 shadow-[0_30px_90px_-50px_rgba(15,23,42,0.7)] backdrop-blur">
          <p className="text-xs font-semibold uppercase tracking-[0.28em] text-cyan-700">Reporting</p>
          <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">Revenue dashboard</h1>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-600">
            Use this screen for recurring revenue telemetry. If reporting services are unavailable, fall back to the overview, subscriptions, invoices, and reconciliation workspaces to keep operating the platform.
          </p>
        </section>

        {/* StatCards row */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard
            label="MRR"
            value={latestMrr ? formatCurrency(latestMrr.mrr) : '—'}
            loading={mrr.isLoading}
          />
          <StatCard
            label="ARR"
            value={arr.data ? formatCurrency(arr.data.arr) : '—'}
            loading={arr.isLoading}
          />
          <StatCard
            label="Churn Rate"
            value={arr.data ? `${(arr.data.churnRate * 100).toFixed(1)}%` : '—'}
            loading={arr.isLoading}
          />
          <StatCard
            label="New MRR"
            value={latestMrr ? formatCurrency(latestMrr.newMrr) : '—'}
            loading={mrr.isLoading}
          />
        </div>

        {/* MRR Trend LineChart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">MRR Trend — Last 12 Months</h2>
          {mrr.isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {mrr.error && <p className="text-red-500 text-sm">Reporting service unavailable.</p>}
          {mrr.data && (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={mrr.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v: number) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: unknown) => formatCurrency(v as number)} />
                <Legend />
                <Line type="monotone" dataKey="mrr" stroke="#6366f1" strokeWidth={2} dot={false} name="MRR" />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* MRR Breakdown BarChart */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">MRR Breakdown</h2>
          {mrr.data && (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={mrr.data} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v: number) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                <Tooltip formatter={(v: unknown) => formatCurrency(v as number)} />
                <Legend />
                <Bar dataKey="newMrr" stackId="a" fill="#22c55e" name="New" />
                <Bar dataKey="expansionMrr" stackId="a" fill="#6366f1" name="Expansion" />
                <Bar dataKey="contractionMrr" stackId="a" fill="#f59e0b" name="Contraction" />
                <Bar dataKey="churnMrr" stackId="a" fill="#ef4444" name="Churn" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* Cohort Retention Heatmap */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-4">Cohort Retention</h2>
          {cohort.isLoading && <p className="text-gray-400 text-sm">Loading…</p>}
          {cohort.error && <p className="text-red-500 text-sm">Reporting service unavailable.</p>}
          {cohort.data && <CohortRetentionHeatmap data={cohort.data} />}
        </div>
      </div>
    </AdminLayout>
  );
}
